import { Badge } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { LibraryElementContentDto } from "../../autogen/bff-api";
import { criterionTypeLabelKey } from "../procurement/publicProcurementOptions";

/**
 * What kind of requirement a library element is, as a badge next to its title.
 *
 * The type used to live as the first segment of the muted one-line summary, where it shared a clipped
 * two-line box with the weight and the description — and vanished entirely on rows already added to the
 * section. It is the thing a buyer picks on, so it gets its own affordance.
 *
 * A minimum requirement gets none: it has no category, only a response format ("Yes / No"), and that
 * stays in the summary. Every row in a minimum panel is the same kind, so the panel stays consistent
 * with itself.
 */
export const RequirementTypeBadge = ({ content }: { content: LibraryElementContentDto }) => {
  const { t } = useTranslation();

  const label = content.award
    ? t(criterionTypeLabelKey(content.award.type))
    : content.awardSubCriterion
      ? // Which criterion the requirement hangs under — that is what places it.
        t(criterionTypeLabelKey(content.awardSubCriterion.criterionType))
      : content.selection
        ? t(content.selection.type)
        : null;

  if (!label) return null;

  return <TypeBadge>{label}</TypeBadge>;
};

export const TypeBadge = ({ children }: { children: React.ReactNode }) => (
  <Badge
    bg="smBackgroundSecondary"
    color="smMuted"
    fontSize="2xs"
    textTransform="none"
    borderRadius="md"
    px="2"
    fontWeight="medium"
    flexShrink={0}
  >
    {children}
  </Badge>
);
